import { InformationFilled } from '@carbon/icons-react';
import * as React from 'react';
import {
  Accordion,
  Body,
  Input,
  Loading,
  StaticBadge,
  SystemMessage,
} from 'vet-bones/design-system/components';
import { isEqual } from 'vet-bones/utils';

import { PanelProps } from 'src/components/panelStack/PanelStack';
import { FileUploadColumnStats } from 'src/components/resourceDialog/FileUploadColumnStats';
import { PanelWithSidebar } from 'src/components/resourceDialog/PanelWithSidebar';
import { ProjectFileUploadOptionsForm } from 'src/components/resourceDialog/ProjectFileUploadOptionsForm';
import { ResourcePreviewTable } from 'src/components/resourceDialog/ResourcePreviewTable';
import {
  MAX_CSV_PARSE_LINES,
  MAX_CSV_VISIBLE_LINES,
} from 'src/constants/limits';
import {
  TEST_IDS,
  getProjectFileUploadPanelColumnLabelTestId,
} from 'src/constants/testIds';
import { useProjectResourceDialog } from 'src/containers/ProjectNewResourceDialog/context/useProjectResourceDialog';
import {
  ProjectFileUploadOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';
import { doesFileHaveDuplicateHeaders } from 'src/utils/csv/doesFileHaveDuplicateHeaders';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useProjectFileUploadParser } from 'src/utils/hooks/useProjectFileUploadParser';

import styles from 'src/components/resourceDialog/styles/resourceDisplayPanel.module.scss';

export type FileUploadPanelProps = {
  fileIndex: number;
};

const getReusableCachedStats = (
  metadata: FileCreationMetadata | undefined,
  fileOptions: ProjectFileUploadOptions
) =>
  isEqual(metadata?.fileOptions, fileOptions)
    ? metadata?.cachedFileStats
    : undefined;

export const FileUploadPanel: React.VFC<PanelProps<FileUploadPanelProps>> = ({
  closePanel,
  fileIndex,
}) => {
  const {
    selectedFiles,
    fileMetadata,
    setFileMetadata,
    setFooterOptions,
    setFullSize,
    setDialogTitle,
    setHeaderBackButton,
  } = useProjectResourceDialog();

  const [isPropertiesOpen, setIsPropertiesOpen] = React.useState(true);
  const currentFile = selectedFiles[fileIndex] as
    | (typeof selectedFiles)[number]
    | undefined;
  const currentMetadata = fileMetadata[fileIndex] as
    | (typeof fileMetadata)[number]
    | undefined;

  const [fileName, setFileName] = React.useState(
    currentMetadata?.fileName || currentFile?.name.replace(/\.csv$/i, '') || ''
  );
  const [fileOptions, setFileOptions] =
    React.useState<ProjectFileUploadOptions>(
      currentMetadata?.fileOptions || defaultProjectFileUploadOptions
    );

  const initialFields = React.useRef(currentMetadata?.fields);

  React.useEffect(() => {
    setFileName(currentMetadata?.fileName || '');
    setFileOptions(
      currentMetadata?.fileOptions || defaultProjectFileUploadOptions
    );
    initialFields.current = currentMetadata?.fields;
  }, [fileIndex, currentFile, currentMetadata]);

  const [hasDuplicateHeaders, setHasDuplicateHeaders] = React.useState(false);
  const didFileChange = useDidValueChange(currentFile);
  const didFileOptionsChange = useDidValueChange(fileOptions);

  React.useEffect(() => {
    if (!currentFile) {
      return;
    }

    if (didFileChange || didFileOptionsChange) {
      doesFileHaveDuplicateHeaders(fileOptions)(currentFile).then(
        (hasDupes) => {
          setHasDuplicateHeaders(hasDupes);
        }
      );
    }
  }, [currentFile, didFileChange, didFileOptionsChange, fileOptions]);

  const didHasDuplicateHeadersChange = useDidValueChange(hasDuplicateHeaders);

  React.useEffect(() => {
    if (didHasDuplicateHeadersChange && hasDuplicateHeaders) {
      setFileOptions((prev) => ({
        ...prev,
        header: false,
      }));
    }
  }, [didHasDuplicateHeadersChange, hasDuplicateHeaders]);

  // Only limit parsed rows for large files (partial uploads)
  const isPartialFile = currentFile && currentMetadata?.isPartial;
  const { fullFileStats, errors, fields, isParsing, rows, updateFieldLabel } =
    useProjectFileUploadParser(
      currentFile,
      fileOptions,
      isPartialFile ? MAX_CSV_PARSE_LINES : undefined,
      initialFields.current,
      getReusableCachedStats(currentMetadata, fileOptions)
    );

  const displayedFileStats = didFileOptionsChange
    ? undefined
    : fullFileStats ?? getReusableCachedStats(currentMetadata, fileOptions);

  const latestValues = React.useRef({
    fileName,
    fileOptions,
    fields,
    isParsing,
    rows,
    fullFileStats,
  });
  React.useEffect(() => {
    latestValues.current = {
      fileName,
      fileOptions,
      fields,
      isParsing,
      rows,
      fullFileStats,
    };
  }, [fileName, fileOptions, fields, isParsing, rows, fullFileStats]);

  // The panel has no footer, so pressing back is its only commit point.
  const saveToMetadata = React.useCallback(() => {
    if (!currentFile) {
      return;
    }
    const { fileName, fileOptions, fields, isParsing, rows, fullFileStats } =
      latestValues.current;
    // Mid-parse, and without a separator or quote, the parser holds no rows.
    // Keep the stored parse rather than emptying the resource card; reopening
    // the panel re-parses with the options saved here.
    const didParse =
      !isParsing && Boolean(fileOptions.separator && fileOptions.quote);

    setFileMetadata((prev) => {
      const prevMetadata = prev[fileIndex];
      // Options and fields have to describe the same parse. With no parse of
      // our own we keep the stored fields, so the stored options must stay with
      // them — advancing to the edited ones would pair a ';' parse with ','.
      const parsedOptions = didParse ? fileOptions : prevMetadata?.fileOptions;
      // The default-options parse is the only other writer of `isPartial`, and
      // it has nothing to say about a file that only parses with edited options
      // — for one that errors under the defaults it never sets the flag at all.
      // This parse knows, so record it here.
      let isPartial = prevMetadata?.isPartial;
      if (didParse) {
        // `rows` is capped at the preview limit whenever the file was already
        // known to be partial, so it can only settle this when the full-file
        // total is missing and the stored flag can't answer either.
        isPartial =
          fullFileStats?.totalRows == null
            ? isPartial ?? rows.length > MAX_CSV_PARSE_LINES
            : fullFileStats.totalRows > MAX_CSV_PARSE_LINES;
      }

      // Never leave a resource with options that can't parse it.
      const savedFileOptions = {
        ...defaultProjectFileUploadOptions,
        ...parsedOptions,
        separator:
          parsedOptions?.separator || defaultProjectFileUploadOptions.separator,
        quote: parsedOptions?.quote || defaultProjectFileUploadOptions.quote,
        escape: parsedOptions?.escape || defaultProjectFileUploadOptions.escape,
      };

      return {
        ...prev,
        [fileIndex]: {
          ...prevMetadata,
          fields: didParse ? fields : prevMetadata?.fields ?? [],
          rowCount: didParse ? rows.length : prevMetadata?.rowCount ?? 0,
          csvPreviewRows: didParse
            ? rows.slice(0, MAX_CSV_PARSE_LINES)
            : prevMetadata?.csvPreviewRows,
          fileName,
          fileOptions: savedFileOptions,
          isPartial,
          cachedFileStats:
            fullFileStats ??
            getReusableCachedStats(prevMetadata, savedFileOptions),
        },
      };
    });
  }, [setFileMetadata, fileIndex, currentFile]);

  const handleBack = React.useCallback(() => {
    saveToMetadata();

    setFullSize(false);
    if (closePanel) {
      closePanel();
    }
  }, [closePanel, saveToMetadata, setFullSize]);

  React.useEffect(() => {
    setFullSize(true);
    setDialogTitle(copy.components.projectResourceDialog.fileUploadPanel.title);
    setHeaderBackButton(() => handleBack);
    setFooterOptions({});

    return () => {
      setHeaderBackButton(null);
      setFullSize(false);
    };
  }, [
    setFullSize,
    setDialogTitle,
    setHeaderBackButton,
    handleBack,
    setFooterOptions,
  ]);

  const hasErrors = errors.length > 0;
  const visibleRows = React.useMemo(
    () => rows.slice(0, MAX_CSV_VISIBLE_LINES),
    [rows]
  );

  const preview = React.useMemo(() => {
    if (isParsing) {
      return (
        <div className={styles.previewLoading}>
          <Loading size="lg" />
        </div>
      );
    }
    if (hasErrors) {
      return (
        <div>
          <SystemMessage
            text={
              <>
                <Body size="xl">
                  {
                    copy.components.projectResourceDialog.projectFileUploadPanel
                      .errors.title
                  }
                </Body>
                {errors.map((error) => (
                  <div key={`error-${error.row}-${error.message}`}>
                    {
                      copy.components.projectResourceDialog
                        .projectFileUploadPanel.errors.row
                    }{' '}
                    {error.row}: {error.message}
                  </div>
                ))}
              </>
            }
            variant="alert"
          />
        </div>
      );
    }
    if (!fileOptions.separator || !fileOptions.quote || !fileOptions.escape) {
      return (
        <SystemMessage
          text={
            copy.components.projectResourceDialog.fileUploadPanel
              .fillAllProperties
          }
          variant="alert"
        />
      );
    }
    if (rows.length === 0) {
      return (
        <SystemMessage
          text={
            copy.components.projectResourceDialog.projectFileUploadPanel.errors
              .empty
          }
          variant="text"
        />
      );
    }
    let lastModified: string | null = null;
    if (currentFile?.lastModified != null) {
      const lastModifiedDate = new Date(currentFile.lastModified);
      const month = String(lastModifiedDate.getMonth() + 1).padStart(2, '0');
      const day = String(lastModifiedDate.getDate()).padStart(2, '0');
      const year = lastModifiedDate.getFullYear();
      lastModified = `${month}.${day}.${year}`;
    }

    return (
      <>
        <div className={styles.fileInfo}>
          <div className={styles.headerRow}>
            <Input
              className={styles.fileName}
              clearable={false}
              id="file-name-input"
              label=""
              minimal
              onChange={(e) => {
                setFileName(e.target.value);
              }}
              placeholder={
                copy.components.projectResourceDialog.fileUploadPanel
                  .fileNamePlaceholder
              }
              size="lg-inside"
              value={fileName}
            />
            <div className={styles.headerRight}>
              {lastModified ? (
                <div className={styles.lastUpdated}>
                  {copy.components.projectResourceDialog.fileUploadPanel.lastUpdated(
                    lastModified
                  )}
                </div>
              ) : null}
              {rows.length >= MAX_CSV_VISIBLE_LINES ? (
                <>
                  {lastModified ? (
                    <span className={styles.headerDivider}>/</span>
                  ) : null}
                  <StaticBadge
                    leftIcon={InformationFilled}
                    size="md"
                    type="default"
                    variant="muted"
                  >
                    {
                      copy.components.projectResourceDialog.fileUploadPanel
                        .mayNotReflectFullData
                    }
                  </StaticBadge>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <ResourcePreviewTable
          fields={fields}
          headerTestIdFn={getProjectFileUploadPanelColumnLabelTestId}
          onFieldLabelChange={updateFieldLabel}
          renderHeaderExtra={(field) => (
            <FileUploadColumnStats
              field={field}
              fields={fields}
              fullFileStats={displayedFileStats}
            />
          )}
          rows={visibleRows}
          testId={TEST_IDS.projectFileUploadPanel.table}
        />
      </>
    );
  }, [
    isParsing,
    hasErrors,
    errors,
    fileOptions.separator,
    fileOptions.quote,
    fileOptions.escape,
    displayedFileStats,
    rows.length,
    currentFile?.lastModified,
    fileName,
    visibleRows,
    fields,
    updateFieldLabel,
  ]);

  if (!currentFile) {
    return (
      <div className={styles.fallbackContainer}>
        <SystemMessage
          text={
            copy.components.projectResourceDialog.fileUploadPanel.fileNotFound
          }
          variant="text"
        />
      </div>
    );
  }

  const asideHeader = (
    <Input
      clearable={false}
      disabled
      label={copy.components.projectResourceDialog.fileUploadPanel.file}
      size="md"
      value={currentFile?.name || ''}
    />
  );

  const asideContent = (
    <Accordion
      icon="left"
      isOpen={isPropertiesOpen}
      label={
        copy.components.projectResourceDialog.fileUploadPanel.resourceProperties
      }
      onInteraction={setIsPropertiesOpen}
      size="md"
    >
      <ProjectFileUploadOptionsForm
        disableHeaderToggle={hasDuplicateHeaders}
        fileOptions={fileOptions}
        onSetFileOptions={setFileOptions}
      />
    </Accordion>
  );

  return (
    <PanelWithSidebar
      asideContent={asideContent}
      asideHeader={asideHeader}
      mainContent={preview}
    />
  );
};
